import React, { useState } from "react";
import { message } from "antd";
import { ArrowLeft, Lock, Eye, EyeOff, KeyRound } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/auth.service";
import pmLogo from "../assets/login/Logo.png";
import bannerImage from "../assets/login/banner.png";
import styles from "./ResetPasswordPage.module.css";

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const validateForm = () => {
    const newErrors: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      newErrors.password = "Vui lòng nhập mật khẩu";
    } else if (password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Vui lòng xác nhận mật khẩu";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Mật khẩu xác nhận không khớp";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) setErrors({ ...errors, password: undefined });
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!email) {
      message.error("Thiếu thông tin xác thực. Vui lòng quay lại trang quên mật khẩu.");
      navigate("/forgot-password");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(email, password);
      message.success("Đặt lại mật khẩu thành công!");
      navigate("/login");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Đặt lại mật khẩu thất bại. Vui lòng thử lại."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Left Section - Banner */}
      <div className={styles.leftColumn}>
        <div className={styles.bannerWrapper}>
          <img src={bannerImage} alt="Reset Password Illustration" className={styles.bannerImage} />
        </div>
      </div>

      {/* Right Section - Form */}
      <div className={styles.rightColumn}>
        <div className={styles.rightCard}>
          <button
            type="button"
            onClick={() => navigate("/otp-verification?email=" + encodeURIComponent(email))}
            className={styles.backButton}
          >
            <ArrowLeft size={18} />
            <span>Quay lại</span>
          </button>

          <div className={styles.formContainer}>
            <img src={pmLogo} alt="Logo" className={styles.logo} />

            <div className={styles.header}>
              <KeyRound size={48} className={styles.icon} />
              <h2 className={styles.title}>Đặt lại mật khẩu</h2>
              <p className={styles.description}>
                Vui lòng nhập mật khẩu mới cho tài khoản của bạn
              </p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Mật khẩu</label>
                <div className={styles.inputWrapper}>
                  <div className={styles.inputContainer}>
                    <Lock className={styles.inputIcon} size={20} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Nhập mật khẩu"
                      className={`${styles.input} ${errors.password ? styles.inputError : ""}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className={styles.eyeIcon} size={20} /> : <Eye className={styles.eyeIcon} size={20} />}
                    </button>
                  </div>
                  {errors.password && <div className={styles.errorText}>{errors.password}</div>}
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>Xác nhận mật khẩu</label>
                <div className={styles.inputWrapper}>
                  <div className={styles.inputContainer}>
                    <Lock className={styles.inputIcon} size={20} />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="Xác nhận mật khẩu"
                      className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ""}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className={styles.eyeIcon} size={20} /> : <Eye className={styles.eyeIcon} size={20} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <div className={styles.errorText}>{errors.confirmPassword}</div>}
                </div>
              </div>

              <button type="submit" disabled={loading} className={styles.submitButton}>
                {loading ? "Đang xử lý..." : "Đặt lại mật khẩu"}
              </button>
            </form>

            <div className={styles.backToLoginContainer}>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className={styles.backToLoginButton}
              >
                Trở về Đăng nhập
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;